import type React from 'react';

import { InputAdornment as MuiInputAdornment } from '@material-ui/core';

import clsx from 'clsx';

import type { ValidationStatus } from '@core/forms/FormControl/types';

import getInputAdornmentCss, { classes } from './getInputAdornmentCss';

export type InputAdornmentProps = {
  /**
   * Defines position of the component
   * @default start
   */
  position?: 'start' | 'end';

  /**
   * Defines variant of the component
   * @default standard
   */
  variant?: 'filled' | 'standard';

  /**
   * Defines children of the component
   */
  children: React.ReactNode;

  /**
   * Defines additional classes for a root component
   */
  className?: string;

  /**
   * Defines validation status
   */
  validationStatus?: ValidationStatus;

  /**
   * Defines vertical alignment of content inside
   * @default center
   */
  verticallyAligned?: 'top' | 'center';

  /**
   * If `true` the border will be displayed on the one of the sides based on the position
   */
  hasSeparator?: boolean;
} & React.ComponentPropsWithRef<'div'>;

/**
 * Renders input field Prefix/Suffix
 */
const InputAdornment = (props: InputAdornmentProps) => {
  const {
    position = 'start',
    variant = 'standard',
    validationStatus,
    className,
    hasSeparator,
    verticallyAligned, // eslint-disable-line
    'aria-hidden': ariaHidden = true,
    ...rest
  } = props;
  return (
    <MuiInputAdornment
      aria-hidden={ariaHidden}
      css={getInputAdornmentCss}
      {...rest}
      disableTypography
      className={clsx(
        verticallyAligned && classes[verticallyAligned],
        classes[variant],
        {
          [classes.valid]: validationStatus === 'success',
          [classes.invalid]: validationStatus === 'error',
          [classes.start]: position === 'start',
          [classes.end]: position === 'end',
          [classes.hasSeparator]: hasSeparator,
        },
        className
      )}
      classes={{
        root: classes.root,
      }}
      position={position}
    />
  );
};

export default InputAdornment;
